import { Link } from '@/core/i18n/navigation';
import { contentDate } from '@/lib/content-date';
import { getPage, pages } from '@/lib/content-registry';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { StructuredData } from '@/components/control-resonant/structured-data';

import { HomeReading } from './control-home-reading';

export function ControlHome() {
  const manifest = pages.map((page) => getPage(page.url)!);
  const zh = getLocale() === 'zh';
  const puzzles = manifest.filter((page) => page.url.startsWith('/puzzles/'));
  const abilities = manifest.filter((page) =>
    page.url.startsWith('/abilities/')
  );
  const questions = [
    { q: m['control.faq.1.q'](), a: m['control.faq.1.a']() },
    { q: m['control.faq.2.q'](), a: m['control.faq.2.a']() },
    { q: m['control.faq.3.q'](), a: m['control.faq.3.a']() },
    { q: m['control.faq.4.q'](), a: m['control.faq.4.a']() },
  ];
  const faq = questions.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  }));
  return (
    <>
      <StructuredData
        data={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faq,
        }}
      />
      <section className="crt-hero crt-container">
        <div>
          <p className="crt-eyebrow">{m['control.hero.eyebrow']()}</p>
          <h1>{manifest[0].h1}</h1>
          <p className="crt-lead">{m['control.hero.body']()}</p>
          <div className="crt-actions">
            <Link href="/build-planner" className="crt-button">
              {m['control.open_planner']()} ↗
            </Link>
            <Link href="/puzzles" className="crt-button crt-button-secondary">
              {m['control.browse_puzzles']()}
            </Link>
          </div>
          <p className="crt-muted">
            {m['control.hero.note']()} · {m['control.updated']()}:{' '}
            <time dateTime={manifest[0].updatedAt}>
              {contentDate(manifest[0].updatedAt, zh ? 'zh' : 'en')}
            </time>
          </p>
        </div>
        <div className="crt-hero-art" aria-hidden="true">
          <div className="crt-orbit" />
          <img src="/logo-mark.svg" width="240" height="240" alt="" />
          <span>01 / RESONANCE</span>
        </div>
      </section>
      <section className="crt-section crt-container">
        <h2 className="crt-eyebrow">{m['control.quick_tools']()}</h2>
        <div className="crt-grid">
          {[
            [
              '/build-planner',
              m['control.nav.planner'](),
              m['control.tools.planner'](),
            ],
            [
              '/puzzles',
              m['control.nav.puzzles'](),
              m['control.tools.puzzles'](),
            ],
            [
              '/abilities',
              m['control.nav.abilities'](),
              m['control.tools.abilities'](),
            ],
            ['/quests', m['control.nav.quests'](), m['control.tools.quests']()],
          ].map(([href, title, body], index) => (
            <Link href={href} className="crt-card" key={href}>
              <span className="crt-card-index">0{index + 1} ↗</span>
              <h3>
                {zh
                  ? [
                      m['control.tools.planner.title'](),
                      m['control.tools.puzzles.title'](),
                      m['control.nav.abilities'](),
                      m['control.tools.quests.title'](),
                    ][index]
                  : title}
              </h3>
              <p>{body}</p>
              {zh && (
                <span className="crt-text-link">
                  {
                    [
                      m['control.tools.planner.cta'](),
                      m['control.tools.puzzles.cta'](),
                      m['control.tools.abilities.cta'](),
                      m['control.tools.quests.cta'](),
                    ][index]
                  }{' '}
                  ↗
                </span>
              )}
            </Link>
          ))}
        </div>
      </section>
      <section className="crt-section crt-container">
        <div className="crt-section-heading">
          <h2>{m['control.popular_puzzles']()}</h2>
          <Link href="/puzzles">{m['control.view_all']()} ↗</Link>
        </div>
        <div className="crt-grid crt-grid-three">
          {puzzles.map((page, index) => (
            <Link key={page.url} href={page.url} className="crt-card">
              <p className="crt-eyebrow">
                {zh ? '秘密藏身处' : 'SECRET HIDEOUT'} / 0{index + 1}
              </p>
              <h3>{page.h1}</h3>
              <p>
                {
                  (zh
                    ? [
                        '洗衣机：开 / 开 / 开 / 关 / 关 / 靠门一台开',
                        '收银机密码：1213',
                        '跑步机 → 健身车 → 自动售货机，共五步',
                        '七个物件，开启一间隐藏房间',
                        '灯柱：中间 / 左侧 / 左侧 / 中间',
                        '收银机密码：BDAC',
                      ]
                    : [
                        'Machine pattern: ON / ON / ON / OFF / OFF / nearest door',
                        'Register code: 1213',
                        'Five actions: treadmill → bike → vending machine',
                        'Seven objects, one hidden room',
                        'Four pillars: middle / left / left / middle',
                        'Register code: BDAC',
                      ])[index]
                }
              </p>
            </Link>
          ))}
        </div>
      </section>
      <section className="crt-section crt-container">
        <h2>{m['control.ability_choices']()}</h2>
        <div className="crt-grid crt-grid-three">
          {abilities.map((page) => (
            <Link className="crt-card" key={page.url} href={page.url}>
              <h3>{page.h1}</h3>
              <p>{page.description}</p>
              <span className="crt-text-link">{m['control.compare']()} ↗</span>
            </Link>
          ))}
        </div>
      </section>
      <section className="crt-section crt-container">
        <h2>{m['control.progression']()}</h2>
        <div className="crt-grid crt-grid-three">
          {manifest
            .filter((page) =>
              [
                '/quests/power-lines',
                '/quests/into-the-sinkhole',
                '/guides/build-power',
              ].includes(page.url)
            )
            .map((page) => (
              <Link className="crt-card" key={page.url} href={page.url}>
                <h3>{page.h1}</h3>
                <p>{page.description}</p>
              </Link>
            ))}
        </div>
      </section>
      <section className="crt-container crt-planner-cta">
        <p className="crt-eyebrow">{m['control.nav.planner']()}</p>
        <h2>{m['control.cta.title']()}</h2>
        <p>{m['control.cta.body']()}</p>
        <Link className="crt-button" href="/build-planner">
          {m['control.open_planner']()} ↗
        </Link>
      </section>
      <HomeReading />
      <section className="crt-section crt-container crt-faq">
        <h2>{m['control.faq.title']()}</h2>
        {questions.map((item, index) => (
          <details key={item.q}>
            <summary>{item.q}</summary>
            <p>
              {item.a}
              {index === 3 && (
                <>
                  {' '}
                  <Link href="/guides/build-power">
                    {zh ? 'Build Power 详解' : 'Build Power'} ↗
                  </Link>
                </>
              )}
            </p>
          </details>
        ))}
      </section>
    </>
  );
}
